/**
 * Product grid scroll-reveal.
 *
 * Progressive enhancement: product cards animate in (fade + rise) as they
 * scroll into view. Entirely additive — cards are fully visible by default
 * via CSS, and this script only ever hides a card immediately before it also
 * registers that same card with the observer that reveals it again. If this
 * script fails to load or errors out, cards simply keep their default
 * visible state, so a grid can never be left invisible.
 */

if (
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
  'IntersectionObserver' in window
) {
  const revealed = new WeakSet();

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const card = entry.target;
        card.style.transition =
          'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)';
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
        observer.unobserve(card);
        revealed.add(card);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  const primeCard = (card) => {
    if (revealed.has(card) || card.dataset.gridRevealPrimed) return;
    card.dataset.gridRevealPrimed = 'true';
    card.style.opacity = '0';
    card.style.transform = 'translateY(28px)';
    observer.observe(card);
  };

  const scan = (root) => {
    if (!root.querySelectorAll) return;
    root.querySelectorAll('product-card.product-card').forEach(primeCard);
  };

  scan(document);

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('product-card.product-card')) primeCard(node);
        scan(node);
      }
    }
  });

  if (document.body) {
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }
}
